import {
  AIBusinessProfile,
  AISitePlan,
  AIGenerateSectionRequest,
  AIRewriteCopyRequest,
  AIJobProgress,
  PageSection,
  SiteSEO,
  IndustryType,
  SectionType,
} from '../types';
import { SiteStructureService } from './siteStructureService';

export class AIClientService {
  /**
   * Generates a complete site plan with progressive status callback
   */
  public async generateSitePlan(
    profile: AIBusinessProfile,
    onProgress?: (progress: AIJobProgress) => void
  ): Promise<AISitePlan> {
    const notify = (stage: any, percent: number, message: string, details?: string) => {
      if (onProgress) {
        onProgress({ stage, progressPercent: percent, message, details });
      }
    };

    notify('ANALYSIS', 15, 'Sektörel işletme profili ve marka tonu analiz ediliyor...', `${profile.companyName} (${profile.industry === 'YACHT_RENTAL' ? 'Yat Kiralama & Charter' : 'Kurumsal Holding & Şirket'})`);
    await new Promise((r) => setTimeout(r, 400));

    notify('DESIGN_PALETTE', 35, 'Tasarım yönü, renk paleti ve tipografi harmonisi seçiliyor...', `${profile.vibe || 'Modern & Prestijli'} tema parametreleri oluşturuluyor.`);
    await new Promise((r) => setTimeout(r, 400));

    notify('SITE_ARCHITECTURE', 55, 'Sayfa ağacı ve menü hiyerarşisi kurgulanıyor...', 'Ana Sayfa, Kurumsal Tanıtım, Filo/Hizmetler ve İletişim rotaları');
    await new Promise((r) => setTimeout(r, 400));

    notify('CONTENT_GENERATION', 75, 'Bileşen şemaları ve sektörel ikna edici metinler yazılıyor...', 'Hero karşılama, istatistikler, özellikler ve görsel bloklar');

    try {
      const response = await fetch('/api/ai/generate-site', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();
      if (json.success && json.data) {
        notify('SEO_OPTIMIZATION', 90, 'Google SEO meta etiketleri ve OpenGraph verileri yapılandırılıyor...');
        await new Promise((r) => setTimeout(r, 300));

        const rawData = json.data as AISitePlan;
        // Ensure plan contains full structure (6 or 7 pages)
        const isYacht = (rawData.industry || profile.industry) === 'YACHT_RENTAL';
        const requiredCount = isYacht ? 7 : 6;
        if (!rawData.pages || rawData.pages.length < requiredCount) {
          const struct = SiteStructureService.buildStructure({
            siteId: '',
            siteName: rawData.name || profile.companyName,
            slug: rawData.slug,
            industry: rawData.industry || profile.industry,
            colorPalette: rawData.colorPalette,
            typography: rawData.typography,
            contactInfo: {
              phone: profile.phone,
              email: profile.email,
              address: profile.location,
              whatsapp: profile.whatsapp,
            },
            userPrompt: profile.prompt,
          });
          rawData.pages = struct.pages;
          if (!rawData.settings) rawData.settings = {} as any;
          rawData.settings.headerMenu = struct.headerMenu;
        }

        notify('COMPLETED', 100, 'Web siteniz başarıyla oluşturuldu!', 'Tüm sayfalar ve bölümler editöre yükleniyor...');
        return rawData;
      }
      throw new Error(json.error?.message || 'Geçersiz AI yanıtı alındı.');
    } catch (err) {
      console.warn('Backend AI endpoint fallback, generating client-side structured plan:', err);

      notify('SECTOR_MODULES', 85, 'Sektör şablonu ve veri mimarisi entegre ediliyor...');
      await new Promise((r) => setTimeout(r, 300));

      const fallbackPlan = this.generateLocalFallbackPlan(profile);
      notify('COMPLETED', 100, 'Web siteniz başarıyla oluşturuldu!');
      return fallbackPlan;
    }
  }

  /**
   * Generates a single custom section via AI
   */
  public async generateSection(
    sectionType: SectionType,
    prompt: string,
    industry: IndustryType,
    siteName: string
  ): Promise<PageSection> {
    try {
      const response = await fetch('/api/ai/generate-section', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionType, prompt, industry, siteName }),
      });

      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data) return json.data;
      }
    } catch (err) {
      console.warn('AI section generate endpoint fallback:', err);
    }

    return {
      id: `sec_${Date.now().toString(36)}`,
      type: sectionType,
      name: `${sectionType} Bölümü`,
      order: 1,
      isVisible: true,
      content: {
        badge: 'AI Özelleştirilmiş',
        title: prompt || 'Özel Bölüm Başlığı',
        description: 'Bu bölüm yapay zeka tarafından siteniz için özel olarak oluşturulmuştur.',
        buttonText: 'İncele',
        buttonLink: '#',
      },
      style: {
        backgroundColor: '#0F172A',
        textColor: '#FFFFFF',
        paddingTop: 'py-20',
        paddingBottom: 'py-20',
        textAlign: 'center',
      },
    };
  }

  /**
   * Rewrites or improves copy
   */
  public async rewriteCopy(
    text: string,
    action: AIRewriteCopyRequest['action'],
    context?: string
  ): Promise<string> {
    try {
      const response = await fetch('/api/ai/rewrite-copy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, action, context }),
      });

      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data?.text) return json.data.text;
      }
    } catch (err) {
      console.warn('AI rewrite copy endpoint fallback:', err);
    }

    if (action === 'EXPAND') return `${text} - Sektör lideri standartlarımız ve müşteri odaklı vizyonumuz ile her adımda kusursuz deneyim sunuyoruz.`;
    if (action === 'SUMMARIZE') return text.split('.')[0] || text;
    if (action === 'PUNCHY') return `✨ ${text.toUpperCase()}`;
    if (action === 'TRANSLATE_EN') return `Premium Services & Solutions for ${text}`;
    return text;
  }

  /**
   * Generates SEO metadata
   */
  public async generateSeo(
    siteName: string,
    industry: IndustryType,
    description?: string
  ): Promise<SiteSEO> {
    try {
      const response = await fetch('/api/ai/generate-seo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteName, industry, description }),
      });

      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data) return json.data;
      }
    } catch (err) {
      console.warn('AI generate SEO endpoint fallback:', err);
    }

    const isYacht = industry === 'YACHT_RENTAL';
    return {
      title: `${siteName} | ${isYacht ? 'Lüks Yat Kiralama & VIP Mavi Tur' : 'Kurumsal Hizmetler & Danışmanlık'}`,
      description: `${siteName} - ${isYacht ? 'Ege ve Akdeniz koylarında lüks yat kiralama.' : 'Kurumsal danışmanlık ve profesyonel çözümler.'}`,
      keywords: isYacht ? ['yat kiralama', 'mavi tur', 'gulet', 'katamaran'] : ['kurumsal', 'danışmanlık', 'holding', 'iş çözümleri'],
      ogImage: isYacht
        ? 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?auto=format&fit=crop&w=1200&q=80'
        : 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
    };
  }

  public async generateSEO(
    siteName: string,
    industry: IndustryType,
    description?: string
  ): Promise<SiteSEO> {
    return this.generateSeo(siteName, industry, description);
  }

  private generateLocalFallbackPlan(profile: AIBusinessProfile): AISitePlan {
    const isYacht = profile.industry === 'YACHT_RENTAL';
    const name = profile.companyName || (isYacht ? 'Aegean Azure Yachting' : 'Apex Global Holding');
    const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    const location = profile.location || (isYacht ? 'Göcek / Muğla' : 'İstanbul');

    const colorPalette = isYacht
      ? {
          primaryColor: '#0369A1',
          secondaryColor: '#0C4A6E',
          accentColor: '#F59E0B',
          backgroundColor: '#0F172A',
          textColor: '#F8FAFC',
        }
      : {
          primaryColor: '#1E40AF',
          secondaryColor: '#0F172A',
          accentColor: '#10B981',
          backgroundColor: '#FFFFFF',
          textColor: '#0F172A',
        };

    const typography = {
      fontFamily: 'Inter',
      headingFont: 'Plus Jakarta Sans',
    };

    const struct = SiteStructureService.buildStructure({
      siteId: '',
      siteName: name,
      slug,
      industry: profile.industry,
      colorPalette,
      typography,
      contactInfo: {
        phone: profile.phone,
        email: profile.email,
        address: location,
        whatsapp: profile.whatsapp,
      },
      userPrompt: profile.prompt,
    });

    return {
      name,
      slug,
      industry: profile.industry,
      vibe: profile.vibe || 'MODERN_MINIMAL',
      colorPalette,
      typography,
      settings: {
        siteName: name,
        logoText: name,
        primaryColor: colorPalette.primaryColor,
        secondaryColor: colorPalette.secondaryColor,
        accentColor: colorPalette.accentColor,
        phone: profile.phone || '',
        email: profile.email || '',
        address: location || '',
        headerMenu: struct.headerMenu,
        headerButtonText: isYacht ? 'Rezervasyon Yap' : 'İletişime Geçin',
        headerButtonAction: {
          type: 'page',
          targetPage: isYacht ? '/rezervasyon' : '/iletisim',
        },
        socialLinks: {
          whatsapp: profile.whatsapp || profile.phone || '',
        },
      },
      seo: {
        title: `${name} | ${isYacht ? 'Lüks Yat Kiralama' : 'Kurumsal Web Sitesi'}`,
        description: `${name} resmi web sitesi.`,
        keywords: isYacht ? ['yat kiralama', 'mavi tur'] : ['kurumsal danışmanlık'],
      },
      pages: struct.pages,
    };
  }
}

export const aiClientService = new AIClientService();
export default aiClientService;
